/** Country names for the voyage map, and a placer that keeps map labels from overlapping each other or the stops. */

export type LabelKind = 'country' | 'island';
/** `minor` names (countries with no stop) are left off phone-sized maps. */
export type MapName = { text: string; lon: number; lat: number; kind: LabelKind; minor?: true };
export type LabelCand = { text: string; x: number; y: number; kind: LabelKind };
export type PlacedLabel = LabelCand & { size: number; box: [number, number, number, number] };

const C = (text: string, lon: number, lat: number): MapName => ({ text, lon, lat, kind: 'country' });

/** Countries along the route, set in open water or across the main island. */
export const MAP_NAMES: MapName[] = [
  C('New Caledonia', 165.2, -20.4),
  C('Vanuatu', 170.9, -16.4),
  C('Solomon Islands', 161.8, -7.4),
  { ...C('Papua New Guinea', 143.6, -5.9), minor: true },
  C('Micronesia', 152.6, 9.6),
  C('Palau', 132.8, 8.6),
  C('Indonesia', 121.0, -2.2),
  C('Philippines', 121.3, 15.7),
  C('Malaysia', 113.8, 2.6),
  C('Singapore', 103.8, 0.4), { ...C('Australia', 134, -18.6), minor: true },
];

/** Pixel sizes and average character widths in ems: countries are spaced-out mono capitals (by CSS). */
const SIZE: Record<LabelKind, number> = { country: 11, island: 11.5 };
const EM: Record<LabelKind, number> = { country: 0.74, island: 0.56 };
const overlaps = (a: number[], b: number[]) => a[0]! < b[2]! && a[2]! > b[0]! && a[1]! < b[3]! && a[3]! > b[1]!;

/**
 * Places labels in map units (`px` units per screen pixel), countries first, each at its spot or just above,
 * below, right or left of it. A label that would overlap another label or a dot, or leave the view, is dropped;
 * a name already placed from an earlier spot is not placed again.
 */
export function placeLabels(cands: LabelCand[], px: number, dots: [number, number, number][], vb: [number, number, number, number]): PlacedLabel[] {
  const taken = dots.map(([x, y, r]) => [x - r, y - r, x + r, y + r]);
  const order = [...cands].sort((a, b) => Number(a.kind === 'island') - Number(b.kind === 'island'));
  const out: PlacedLabel[] = [];
  for (const c of order) {
    if (out.some(p => p.text === c.text)) continue;
    const size = SIZE[c.kind] * px, w = c.text.length * size * EM[c.kind] + size * 0.4, hh = size * 1.2, gap = size * 0.5;
    for (const [dx, dy] of [[0, 0], [0, -(hh + gap)], [0, hh + gap], [w / 2 + gap, 0], [-(w / 2 + gap), 0]] as const) {
      const x = c.x + dx, y = c.y + dy;
      const box: [number, number, number, number] = [x - w / 2, y - hh / 2, x + w / 2, y + hh / 2];
      if (box[0] < vb[0] || box[1] < vb[1] || box[2] > vb[0] + vb[2] || box[3] > vb[1] + vb[3]) continue;
      if (taken.some(t => overlaps(t, box))) continue;
      taken.push(box);
      out.push({ ...c, x, y, size, box });
      break;
    }
  }
  return out;
}
